import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Flame, ListChecks, Wallet, ShieldAlert, TrendingUp } from "lucide-react";
import AppHero from "../components/AppHero";
import FaqAccordion from "../components/FaqAccordion";
import { GameCard } from "../components/GameGrid";
import { useData } from "../data/DataContext";
import Seo from "../components/Seo";


const faqs = [
  { q: "What is the difference between Yono All Games and the home page list?", a: "The home page splits apps into Old and New tabs. Yono All Games puts every tracked app on a single scroll with the same card layout, so you can compare bonuses side by side without switching tabs." },
  { q: "How often is the Yono All Games list refreshed?", a: "We re-check download links and bonus amounts regularly. When an app changes its welcome offer or a link stops working, the card here is updated and the app page gets a fresh note." },
  { q: "Can I use one login for all the apps on this page?", a: "No. Each Yono app is a separate product with its own wallet, so you register on each one with your mobile number and OTP." },
  { q: "Which apps on this page are rummy-focused?", a: "Look for names with Rummy in them - Rummy Zip, Ok Rummy, Gold Rummy, Top Rummy and IND Rummy are the rummy-first picks. Slots-first apps carry Slots, Spin or 777 in the name." },
  { q: "Is there a risk in installing many APKs at once?", a: "Each APK takes 50-70 MB and asks for install permission. Install only the two or three apps you plan to use, and remove any you stop playing to keep your phone clean." },
];

const trending = [
  "yono all games apk", "yono all games list 2026", "all yono games app", "rummy app ₹100 withdrawal", "new yono app bonus",
  "yono 777", "yono vip", "yono rummy", "diwa 777", "ok rummy", "spin 101", "jaiho 777", "ind slots", "rummy 888", "yono arcade",
];

const YonoAllGames = () => {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const { allGames, downloadUrl: DOWNLOAD_URL, gameDownloadUrl } = useData();
  const handleDownload = () => window.open(DOWNLOAD_URL, "_blank", "noopener");

  return (
    <div data-testid="yono-all-games-page" className="max-w-4xl mx-auto px-4 py-8">
      <Seo
        title="Yono All Games APK Download 2026 – Full List, Bonus & Withdrawal | Maha Games Apps"
        description="Yono All Games index for 2026: every Yono rummy, slots, Teen Patti and bingo app on one scroll with signup bonus, ₹100 withdrawal and official APK download. Compare 80+ apps before you install."
        keywords={trending}
        image="/images/games/yono-all-games-logo-w512.webp"
        schema={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Yono All Games",
          numberOfItems: allGames.length,
          itemListElement: allGames.slice(0, 50).map((gm, i) => ({ "@type": "ListItem", position: i + 1, name: gm.name, url: `${window.location.origin}/game/${gm.slug}` })),
        }}
      />
      <AppHero
        name="Yono All Games"
        logo="/images/games/yono-all-games-logo-w512.webp"
        rating="4.5"
        size="58-65"
        downloads="100K"
        bonus="50"
        onDownload={handleDownload}
        testPrefix="yono-all-games"
        tagline="Yono All Games Official 2026 – Every Yono App, ₹49 to ₹545 Signup Bonus | Instant UPI Withdraw – Download APK"
      />

      <section className="mt-8">
        <h2 className="text-2xl font-extrabold text-white flex items-center gap-2">
          <span className="w-1.5 h-6 rounded-full bg-[#ffb224] inline-block" /> Yono All Games - The Full Index on One Scroll
        </h2>
        <p className="text-emerald-50/70 leading-relaxed mt-3 text-[15px]">
          This page is the long version of our directory: every Yono app we follow, from the slots-heavy 777 titles to rummy rooms like Ok Rummy and Top Rummy, lined up in a single list. Nothing is hidden behind tabs here, which makes it the quickest way to scan welcome offers and spot the apps worth your time.
        </p>

        <div className="grid sm:grid-cols-3 gap-3 mt-6">
          {[
            { icon: ListChecks, title: "80+ apps, one card each", desc: "Name, players, signup bonus and a link to the app's own guide." },
            { icon: Wallet, title: "₹100 payout everywhere", desc: "Every app here pays out from ₹100 to UPI or bank in your own name." },
            { icon: ShieldAlert, title: "Links we checked", desc: "Download buttons go to the app's official APK, never a chat forward." },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="bg-[#073c1b] border border-[#1c5f35] rounded-2xl p-4">
              <Icon size={20} className="text-[#ffb224]" />
              <p className="font-bold text-white text-sm mt-2">{title}</p>
              <p className="text-xs text-emerald-50/70 leading-relaxed mt-1">{desc}</p>
            </div>
          ))}
        </div>

        <p className="text-emerald-50/70 leading-relaxed mt-6 text-[15px]">
          Bonuses on this page run from ₹49 (IND Slots) up to ₹545 (Diwa 777). A bigger number is not automatically the better deal - the bonus sits in a play balance and only winnings leave the app. A sensible plan is to shortlist two or three apps whose games you actually like, deposit a small amount on one, and try a ₹100 withdrawal before going further. Want a rummy-only view? Jump back to the <Link to="/" className="text-[#ffb224] font-semibold underline">home page</Link> and search "rummy".
        </p>

        <h3 className="text-xl font-extrabold text-white mt-12 mb-4 text-center flex items-center justify-center gap-2">
          <Flame size={20} className="text-[#ffb224]" /> Other <span className="text-[#ffb224]">Related</span> Apps
          <span className="bg-[#15562e] text-[#ffb224] text-xs font-bold px-2.5 py-1 rounded-full">{allGames.length} Apps</span>
        </h3>
        <div data-testid="yono-all-games-list" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {allGames.map((gm, i) => (
            <GameCard key={gm.slug} game={gm} rank={i + 1} downloadUrl={gameDownloadUrl(gm)} />
          ))}
        </div>

        <FaqAccordion title="Common Questions about Yono All Games" faqs={faqs} />

        <div className="mt-8 bg-[#073c1b] border border-[#1c5f35] rounded-xl px-4 py-3">
          <p className="text-[13px] leading-relaxed text-emerald-50/80 text-center">
            <span className="font-bold text-white inline-flex items-center gap-1"><TrendingUp size={14} /> Trending now :- </span>
            {trending.join(", ")}
          </p>
        </div>
      </section>
    </div>
  );
};

export default YonoAllGames;
